import React, { useState } from 'react';
import { FiChevronDown, FiChevronRight, FiSearch, FiBook, FiZap, FiImage, FiUsers, FiCreditCard, FiShield, FiMail } from 'react-icons/fi';
import { Link } from 'react-router-dom';

const FAQ_DATA = {
  'Getting Started': {
    icon: FiZap,
    questions: [
      {
        q: 'How do I create my first client?',
        a: 'After signing in, click "Add Client" on your dashboard. You\'ll go through a 6-step wizard to capture your client\'s brand identity, visual style, target audience, goals, constraints, and social presence. This information helps the AI generate perfectly aligned content.'
      },
      {
        q: 'What is a Content Focus?',
        a: 'A Content Focus is a specific creative direction within a campaign. Think of it as the "angle" you want to take. For example, if your campaign is "Summer Launch", your focuses might be "Behind the Scenes", "Customer Stories", or "Product Benefits". Each focus generates different types of content.'
      },
      {
        q: 'How does the AI learn my preferences?',
        a: 'When you approve or reject content ideas, the AI learns from your choices. Approved content patterns are reinforced (70% weight), while the AI also explores new angles (30% weight) to keep your content fresh. The more you use FirstCut, the better it gets!'
      },
      {
        q: 'What\'s the difference between campaigns and content focuses?',
        a: 'Campaigns are high-level initiatives (e.g., "Q1 Brand Awareness"). Content Focuses are specific creative angles within a campaign (e.g., "User Testimonials", "Product Features"). You can have multiple focuses per campaign.'
      }
    ]
  },
  'Content Generation': {
    icon: FiImage,
    questions: [
      {
        q: 'Why is my content being flagged as similar?',
        a: 'FirstCut ensures every idea feels fresh and unique. If you\'ve explored similar concepts before, FirstCut will automatically try different angles. Try using a different Content Focus or creative direction if this persists.'
      },
      {
        q: 'What image quality options are available?',
        a: 'We offer three quality tiers:\n• Draft: Quick previews, lower resolution\n• Standard: Client-ready quality\n• High: Production/print quality, maximum detail'
      },
      {
        q: 'Can I generate content for multiple platforms at once?',
        a: 'Yes! When generating content, you can select multiple platforms (Instagram, Facebook, YouTube) and content types (Posts, Reels, Stories, Carousels). Each combination generates tailored, format-native content.'
      },
      {
        q: 'How do content refinements work?',
        a: 'Click "Refine" on any content card to modify it. You can regenerate just the text, just the image, or both. Use quick suggestions like "More playful" or "Add urgency" or write custom instructions. First 3 refinements per content are free!'
      },
      {
        q: 'What does "format-native" mean?',
        a: 'Format-native means each content type gets a tailored structure:\n• Reels: Story arc, camera movements, pacing\n• Posts: Scroll-stopping visuals, single message\n• Stories: Raw/intimate feel, minimal text\n• Carousels: Journey format, each slide earns the next swipe'
      },
      {
        q: 'Do you generate actual videos?',
        a: 'Not yet! For video content types (Reels, Shorts, YouTube), we generate:\n• Complete script with shot-by-shot breakdown\n• Camera movements and timing\n• Cover/thumbnail image\n\nYou or your video editor can then bring the script to life. Video generation (Sora 2) is coming soon!'
      }
    ]
  },
  'Clients & Teams': {
    icon: FiUsers,
    questions: [
      {
        q: 'How many brands can I manage?',
        a: 'It depends on your plan:\n• Free: 1 brand\n• Starter: 3 brands\n• Growth: 5 brands\n• Scale: 10 brands'
      },
      {
        q: 'Can I add team members?',
        a: 'Team seats are available on Growth (2 seats) and Scale (5 seats) plans. Team features are coming soon!'
      },
      {
        q: 'How do I update brand information?',
        a: 'Go to the brand\'s detail page and click "Edit Brand". You can update any information from the original onboarding wizard. Changes will be reflected in all future content generation.'
      }
    ]
  },
  'Billing & Credits': {
    icon: FiCreditCard,
    questions: [
      {
        q: 'How do credits work?',
        a: 'Credits are used for content generation. Each idea (text + image) uses 12 credits from your balance. Your plan includes a monthly credit allowance, and you can purchase additional credit packs if needed.'
      },
      {
        q: 'What happens if I run out of credits?',
        a: 'You can purchase extra credit packs anytime. Add-on packs expire 90 days from purchase. Starter at ₹5,499 for 100 credits (~8 ideas).'
      },
      {
        q: 'Can I change my plan?',
        a: 'Yes! Go to Usage Dashboard and click "Change Plan". Upgrades take effect immediately. Downgrades take effect at the start of your next billing cycle.'
      },
      {
        q: 'Do unused credits roll over?',
        a: 'No, subscription credits reset each month and don\'t roll over. Add-on credits last 90 days. Plan your content calendar to make the most of your monthly allowance!'
      },
      {
        q: 'What\'s included in the free tier?',
        a: 'Free accounts get 40 one-time credits to try FirstCut (~3 ideas). These don\'t renew monthly - once used, you\'ll need to upgrade to continue generating content. It\'s perfect for testing if FirstCut is right for you!'
      }
    ]
  },
  'Privacy & Security': {
    icon: FiShield,
    questions: [
      {
        q: 'Is my client data secure?',
        a: 'Yes. We use industry-standard encryption for data in transit and at rest. Your client information is never shared with other users or used to train AI models for other accounts.'
      },
      {
        q: 'Can I delete my data?',
        a: 'Yes. You can delete individual clients, campaigns, or content ideas at any time. For complete account deletion, contact our support team.'
      },
      {
        q: 'Do you use my content to train AI?',
        a: 'We only use your approval/rejection patterns to improve recommendations for YOUR account. Your actual content and client data are never used to train models for other users.'
      }
    ]
  }
};

function HelpCenter() {
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCategory, setExpandedCategory] = useState('Getting Started');
  const [expandedQuestion, setExpandedQuestion] = useState(null);

  // Filter FAQs based on search
  const filteredFAQs = Object.entries(FAQ_DATA).reduce((acc, [category, data]) => {
    const filteredQuestions = data.questions.filter(
      q => q.q.toLowerCase().includes(searchQuery.toLowerCase()) ||
           q.a.toLowerCase().includes(searchQuery.toLowerCase())
    );
    if (filteredQuestions.length > 0 || !searchQuery) {
      acc[category] = { ...data, questions: searchQuery ? filteredQuestions : data.questions };
    }
    return acc;
  }, {});

  return (
    <div className="max-w-4xl mx-auto animate-fade-in-up">
      <div className="mb-8">
        <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground">Help Center</h1>
        <p className="text-muted-foreground mt-1">Find answers to common questions about FirstCut</p>
      </div>

      {/* Search */}
      <div className="relative mb-8">
        <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" size={20} />
        <input
          type="text"
          placeholder="Search for answers..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-12 pr-4 py-3 bg-card border border-border/50 rounded-xl text-foreground placeholder:text-muted-foreground focus:ring-2 focus:ring-primary/50 focus:border-primary/50 transition-all"
          data-testid="help-search"
        />
      </div>

      {/* Quick Links */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <Link to="/dashboard" className="p-4 bg-primary/10 rounded-xl hover:bg-primary/20 transition-colors text-center border border-primary/20">
          <FiZap className="mx-auto mb-2 text-primary" size={24} />
          <span className="text-sm font-medium text-foreground">Dashboard</span>
        </Link>
        <Link to="/usage" className="p-4 bg-accent/10 rounded-xl hover:bg-accent/20 transition-colors text-center border border-accent/20">
          <FiCreditCard className="mx-auto mb-2 text-accent" size={24} />
          <span className="text-sm font-medium text-foreground">View Usage</span>
        </Link>
        <Link to="/content" className="p-4 bg-emerald-500/10 rounded-xl hover:bg-emerald-500/20 transition-colors text-center border border-emerald-500/20">
          <FiImage className="mx-auto mb-2 text-emerald-400" size={24} />
          <span className="text-sm font-medium text-foreground">Content Library</span>
        </Link>
        <Link to="/contact" className="p-4 bg-amber-500/10 rounded-xl hover:bg-amber-500/20 transition-colors text-center border border-amber-500/20">
          <FiMail className="mx-auto mb-2 text-amber-400" size={24} />
          <span className="text-sm font-medium text-foreground">Contact Support</span>
        </Link>
      </div>

      {/* FAQ Categories */}
      <div className="space-y-4">
        {Object.entries(filteredFAQs).map(([category, data]) => {
          const IconComponent = data.icon;
          const isExpanded = expandedCategory === category;
          
          return (
            <div key={category} className="border border-border/50 rounded-xl overflow-hidden bg-card">
              <button
                onClick={() => setExpandedCategory(isExpanded ? null : category)}
                className="w-full flex items-center justify-between p-4 bg-secondary/50 hover:bg-secondary transition-colors"
                data-testid={`category-${category.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-card rounded-lg border border-border/30">
                    <IconComponent className="text-primary" size={20} />
                  </div>
                  <span className="font-semibold text-foreground">{category}</span>
                  <span className="text-sm text-muted-foreground">({data.questions.length} articles)</span>
                </div>
                {isExpanded ? <FiChevronDown className="text-muted-foreground" size={20} /> : <FiChevronRight className="text-muted-foreground" size={20} />}
              </button>
              
              {isExpanded && (
                <div className="divide-y divide-border/30">
                  {data.questions.map((item, idx) => {
                    const questionKey = `${category}-${idx}`;
                    const isQuestionExpanded = expandedQuestion === questionKey;
                    
                    return (
                      <div key={idx}>
                        <button
                          onClick={() => setExpandedQuestion(isQuestionExpanded ? null : questionKey)}
                          className="w-full flex items-center justify-between p-4 text-left hover:bg-secondary/30 transition-colors"
                        >
                          <span className="font-medium text-foreground pr-4">{item.q}</span>
                          {isQuestionExpanded ? <FiChevronDown className="flex-shrink-0 text-muted-foreground" /> : <FiChevronRight className="flex-shrink-0 text-muted-foreground" />}
                        </button>
                        {isQuestionExpanded && (
                          <div className="px-4 pb-4">
                            <p className="text-muted-foreground whitespace-pre-line leading-relaxed">{item.a}</p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Still Need Help */}
      <div className="mt-8 p-6 bg-gradient-to-r from-primary/5 to-accent/5 rounded-xl border border-primary/20">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-card rounded-lg border border-border/30">
            <FiBook className="text-primary" size={24} />
          </div>
          <div>
            <h3 className="font-semibold text-foreground mb-1">Still need help?</h3>
            <p className="text-muted-foreground text-sm mb-3">
              Cannot find what you are looking for? Our support team is here to help.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors text-sm font-medium"
            >
              <FiMail size={16} />
              Contact Support
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default HelpCenter;
